import { useState, useCallback, useMemo } from 'react';
import { useColumnPreferences } from '../contexts/ColumnPreferencesContext';

// Available product fields mapping - should match PRODUCT_FIELDS from ColumnManagerDialog
const FIELD_LABELS = {
  // Basic Information
  name: '产品名称',
  product_id: '产品ID',
  part_number: '零件号',
  client_ref_number: '物料编码',
  category: '类别',
  specification: '规格',
  status: '状态',
  remark: '备注',
  
  // Manufacturing Parameters
  material: '材料',
  coating: '涂层',
  diameter: '直径',
  length: '长度',
  hardness: '硬度',
  tolerance: '公差',
  blade_type: '刃型',
  guide_pillar_length: '导柱长度',
  guide_pillar_tolerance: '导柱直径公差',
  marking: '打标',
  
  // Pricing & Inventory
  price: '价格',
  unit: '单位',
  min_order_qty: '最小订货量',
  lead_time: '交期',
  stock_status: '库存状态',
  
  // Quality & Compliance
  certification: '认证',
  quality_grade: '质量等级',
  inspection_standard: '检验标准',
  
  // Common grid columns (for quotation/sales/purchase grids)
  quantity: '数量',
  unit_price: '单价',
  total_price: '总价',
  unit_price_tax_included: '单价(含税)',
  total_price_tax_included: '总价(含税)',
  tax_amount: '税额',
  discount: '折扣',
  delivery_date: '交期',
  supplier: '供应商'
};

/**
 * Hook for managing column preferences in data grids
 * @param {string} view - The view identifier (quotation, sales, purchase, inventory)
 * @param {object} options - Configuration options
 */
export const useColumnManager = (view, options = {}) => {
  const {
    enableResize = true,
    enableExport = true,
    additionalColumns = {},
    onColumnChange
  } = options;

  const {
    getColumns,
    updateColumns,
    resetColumns,
    hasCustomColumns,
    getColumnConfig
  } = useColumnPreferences();

  const [columnManagerOpen, setColumnManagerOpen] = useState(false);

  // Get current columns for the view
  const currentColumns = useMemo(() => {
    return getColumns(view);
  }, [getColumns, view]);

  // Get column configuration
  const columnConfig = useMemo(() => {
    return getColumnConfig(view);
  }, [getColumnConfig, view]);

  // Combined field labels (default + additional)
  const allFieldLabels = useMemo(() => {
    return { ...FIELD_LABELS, ...additionalColumns };
  }, [additionalColumns]);

  // Generate table columns for MUI DataGrid or custom tables
  const tableColumns = useMemo(() => {
    return currentColumns.map(fieldId => ({
      field: fieldId,
      headerName: allFieldLabels[fieldId] || fieldId,
      sortable: true,
      resizable: enableResize,
      hideable: true,
      // Default widths - can be customized per field
      width: getColumnWidth(fieldId),
      // Add custom renderers if needed
      renderCell: (params) => formatCellValue(params.value, fieldId)
    }));
  }, [currentColumns, allFieldLabels, enableResize]);

  // Open column manager dialog
  const openColumnManager = useCallback(() => {
    setColumnManagerOpen(true);
  }, []);

  // Close column manager dialog
  const closeColumnManager = useCallback(() => {
    setColumnManagerOpen(false);
  }, []);

  // Save column configuration
  const saveColumns = useCallback((newColumns) => {
    updateColumns(view, newColumns);
    if (onColumnChange) {
      onColumnChange(newColumns);
    }
  }, [updateColumns, view, onColumnChange]);

  // Reset to default columns
  const resetToDefault = useCallback(() => {
    resetColumns(view);
    if (onColumnChange) {
      onColumnChange(getColumns(view));
    }
  }, [resetColumns, view, onColumnChange, getColumns]);

  // Check if a specific column is visible
  const isColumnVisible = useCallback((columnId) => {
    return currentColumns.includes(columnId);
  }, [currentColumns]);

  // Toggle column visibility
  const toggleColumn = useCallback((columnId) => {
    const newColumns = isColumnVisible(columnId)
      ? currentColumns.filter(col => col !== columnId)
      : [...currentColumns, columnId];
    
    saveColumns(newColumns);
  }, [currentColumns, isColumnVisible, saveColumns]);

  // Get export columns (filtered data based on visible columns)
  const getExportData = useCallback((data) => {
    if (!enableExport || !data) return data;
    
    return data.map(row => {
      const exportRow = {};
      currentColumns.forEach(columnId => {
        if (row.hasOwnProperty(columnId)) {
          exportRow[allFieldLabels[columnId] || columnId] = row[columnId];
        }
      });
      return exportRow;
    });
  }, [currentColumns, allFieldLabels, enableExport]);

  // Get column headers for export
  const getExportHeaders = useCallback(() => {
    return currentColumns.map(columnId => allFieldLabels[columnId] || columnId);
  }, [currentColumns, allFieldLabels]);

  // Column management actions
  const columnActions = useMemo(() => ({
    openManager: openColumnManager,
    closeManager: closeColumnManager,
    save: saveColumns,
    reset: resetToDefault,
    toggle: toggleColumn
  }), [openColumnManager, closeColumnManager, saveColumns, resetToDefault, toggleColumn]);

  return {
    // Current state
    currentColumns,
    tableColumns,
    columnConfig,
    
    // Dialog state
    columnManagerOpen,
    
    // Actions
    ...columnActions,
    
    // Export utilities
    getExportData,
    getExportHeaders,
    
    // Utilities
    isColumnVisible,
    hasCustomColumns: hasCustomColumns(view),
    fieldLabels: allFieldLabels
  };
};

// Helper function to get default column width
const getColumnWidth = (fieldId) => {
  const widthMap = {
    name: 200,
    product_id: 120,
    specification: 180,
    category: 100,
    price: 100,
    unit_price: 100,
    total_price: 120,
    unit_price_tax_included: 120,
    total_price_tax_included: 130,
    quantity: 80,
    unit: 60,
    status: 80,
    material: 100,
    coating: 80,
    diameter: 80,
    length: 80,
    hardness: 80,
    tolerance: 80,
    blade_type: 80,
    marking: 80,
    remark: 150
  };
  
  return widthMap[fieldId] || 120;
};

// Helper function to format cell values
const formatCellValue = (value, fieldId) => {
  if (value === null || value === undefined) {
    return '-';
  }
  
  // Format specific field types
  switch (fieldId) {
    case 'price':
    case 'unit_price':
    case 'total_price':
    case 'unit_price_tax_included':
    case 'total_price_tax_included':
      return typeof value === 'number' ? `¥${value.toFixed(2)}` : value;
    
    case 'quantity':
    case 'min_order_qty':
      return typeof value === 'number' ? value.toLocaleString() : value;
    
    case 'diameter':
    case 'length':
      return typeof value === 'number' ? `${value}mm` : value;
    
    default:
      return value;
  }
};

export default useColumnManager;